import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

interface SessionSummaryMarkdownProps {
    markdown: string;
    className?: string;
    paragraphClassName?: string;
}

type InlineMarkdownToken = {
    type: "text" | "strong" | "em";
    value: string;
};

const INLINE_MARKDOWN_PATTERN =
    /(\*\*[^*\n]+\*\*|__[^_\n]+__|\*[^*\n]+\*|_[^_\n]+_)/g;

function tokenizeInlineMarkdown(markdown: string): InlineMarkdownToken[] {
    const tokens: InlineMarkdownToken[] = [];
    let lastIndex = 0;

    for (const match of markdown.matchAll(INLINE_MARKDOWN_PATTERN)) {
        const value = match[0];
        const index = match.index ?? 0;

        if (index > lastIndex) {
            tokens.push({
                type: "text",
                value: markdown.slice(lastIndex, index),
            });
        }

        if (value.startsWith("**") || value.startsWith("__")) {
            tokens.push({ type: "strong", value: value.slice(2, -2) });
        } else {
            tokens.push({ type: "em", value: value.slice(1, -1) });
        }

        lastIndex = index + value.length;
    }

    if (lastIndex < markdown.length) {
        tokens.push({ type: "text", value: markdown.slice(lastIndex) });
    }

    return tokens;
}

function renderInlineMarkdown(markdown: string): ReactNode[] {
    return tokenizeInlineMarkdown(markdown).map((token, index) => {
        if (token.type === "strong") {
            return (
                <strong key={index} className="font-semibold text-foreground">
                    {token.value}
                </strong>
            );
        }

        if (token.type === "em") {
            return <em key={index}>{token.value}</em>;
        }

        return token.value;
    });
}

export function SessionSummaryMarkdown({
    markdown,
    className,
    paragraphClassName,
}: SessionSummaryMarkdownProps) {
    const paragraphs = markdown
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean);

    if (paragraphs.length === 0) {
        return null;
    }

    return (
        <div className={cn("space-y-1", className)}>
            {paragraphs.map((paragraph, index) => {
                const lines = paragraph.split("\n");
                const bulletItems = lines
                    .map((line) => line.trim())
                    .filter(
                        (line) =>
                            line.startsWith("- ") || line.startsWith("+ "),
                    );

                if (bulletItems.length === lines.length) {
                    return (
                        <ul key={index} className="list-disc space-y-0.5 pl-5">
                            {bulletItems.map((item, itemIndex) => (
                                <li key={itemIndex}>
                                    {renderInlineMarkdown(item.slice(2))}
                                </li>
                            ))}
                        </ul>
                    );
                }

                return (
                    <p key={index} className={paragraphClassName}>
                        {/* AIDEV-NOTE: Limited renderer avoids ReactMarkdown SSR crash path for practitioner summaries. */}
                        {lines.map((line, lineIndex) => (
                            <span key={lineIndex}>
                                {lineIndex > 0 && <br />}
                                {renderInlineMarkdown(line)}
                            </span>
                        ))}
                    </p>
                );
            })}
        </div>
    );
}
